import EditIcon from '@atlaskit/icon/glyph/edit';
import { MenuGroup } from '@atlaskit/menu';
import { PopupProps, TriggerProps } from '@atlaskit/popup';
import Tooltip from '@atlaskit/tooltip';
import Popup from '@databases/atlaskit/Popup';
import { useFlaggingCallback } from '@databases/banners';
import { messages } from '@databases/intl/messages';
import { useComponent, useStateWithOverride } from '@databases/react';
import { DefaultRootState, defaultStoreUtils } from '@databases/redux';
import {
  selectSelectedEntryIds,
  selectSelectedFieldIds,
  selectSelectedValues,
} from '@databases/redux/cellSelections/selectors';
import { DatabaseValue } from '@databases/types/database';
import {
  getDatabaseEntryOptional,
  getDatabaseEntryRequired,
  getDatabaseFieldOptional,
  getDatabaseValue,
} from '@databases/utils/database/utils';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { isEqual } from 'lodash-es';
import React, {
  DetailedHTMLProps,
  FunctionComponent,
  HTMLAttributes,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import DatabaseValueBulkEdit from '../../database-value/DatabaseValueBulkEdit';
import TriggerButton from '../view-settings-picker/TriggerButton';

const ContentContainer = styled.div`
  padding: 4px;
`;

type TriggerPropsWithHtmlProps = TriggerProps &
  DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

type BulkEditValuesProps = {
  contentId: string;
  removeSelectedContent: () => void;
  Trigger?: (props: TriggerPropsWithHtmlProps) => React.ReactElement;
  isOpen?: boolean;
  offset?: [number, number];
  placement?: PopupProps['placement'];
};

const BulkEditValues: FunctionComponent<BulkEditValuesProps> = ({
  contentId,
  removeSelectedContent,
  Trigger,
  isOpen,
  offset,
  placement,
}) => {
  const [selectedValues, initialValue] = defaultStoreUtils.useEqualSelector(
    (state: DefaultRootState) => {
      const database = selectDatabaseByContentIdRequired(state, {
        contentId,
      });
      const selectedFields = selectSelectedFieldIds(state, {
        contentId,
      });
      const selectedEntries = selectSelectedEntryIds(state, {
        contentId,
      });
      const selectedValues = selectSelectedValues(state, {
        contentId,
      });

      const firstSelectedField = getDatabaseFieldOptional(database, selectedFields[0]);
      const firstSelectedEntry = getDatabaseEntryOptional(database, selectedEntries[0]);

      const valuesOfSelectedEntries =
        firstSelectedField &&
        selectedEntries.map((entryId) => {
          const entry = getDatabaseEntryRequired(database, entryId);
          return getDatabaseValue(database, firstSelectedField, entry);
        });

      const allValuesEqual = valuesOfSelectedEntries?.every((v) =>
        isEqual(v.value, valuesOfSelectedEntries?.[0].value)
      );

      const initialValue =
        allValuesEqual && firstSelectedField && firstSelectedEntry
          ? getDatabaseValue(database, firstSelectedField, firstSelectedEntry)
          : undefined;

      return [selectedValues, initialValue] as const;
    }
  );

  const [value, setValue] = useState<DatabaseValue | undefined>(initialValue);
  const prevValue = useRef(value);
  const handleBulkEditChange = useFlaggingCallback(
    (value: DatabaseValue) => {
      if (prevValue.current === undefined) {
        removeSelectedContent();
      }
      prevValue.current = value;
      setValue(value);
    },
    [removeSelectedContent]
  );

  const [isBulkEditOpen, setIsBulkEditOpen] = useStateWithOverride(false, isOpen);
  const handleToggleIsOpen = useFlaggingCallback(() => {
    if (!isBulkEditOpen) {
      setValue(undefined);
    }
    setIsBulkEditOpen(!isBulkEditOpen);
  }, [isBulkEditOpen, setIsBulkEditOpen]);

  const prevSelectedValues = useRef(selectedValues);
  useEffect(() => {
    if (isBulkEditOpen && !isEqual(selectedValues, prevSelectedValues.current)) {
      setValue(undefined);
    }
    prevSelectedValues.current = selectedValues;
  }, [isBulkEditOpen, selectedValues]);

  const { formatMessage } = useIntl();
  const trigger: PopupProps['trigger'] = useComponent(
    ({ ref, ...props }: TriggerProps) => {
      const iconBefore = (
        <EditIcon
          label={formatMessage(messages['bulk-edit-values.label'])}
          size="medium"
        />
      );
      return Trigger && typeof trigger === 'function' ? (
        Trigger({
          ref,
          ...props,
        })
      ) : (
        <Tooltip content={formatMessage(messages['bulk-edit-values.tooltip'])}>
          <TriggerButton
            iconBefore={iconBefore}
            spacing="compact"
            appearance="subtle"
            selected={isBulkEditOpen}
            onClick={(e) => {
              handleToggleIsOpen();
              e.stopPropagation();
              e.preventDefault();
            }}
            ref={ref}
            isActive={isBulkEditOpen}
            {...props}
          />
        </Tooltip>
      );
    },
    [Trigger, formatMessage, handleToggleIsOpen, isBulkEditOpen]
  );

  return (
    <Popup
      autoFocus={false}
      isOpen={isBulkEditOpen}
      onClose={handleToggleIsOpen}
      contentContainer={ContentContainer}
      trigger={trigger}
      placement={placement}
      offset={offset}
      content={({ setInitialFocusRef }) => (
        <MenuGroup
          minWidth="200px"
          maxHeight="435px"
          onClick={(e) => e.stopPropagation()}
        >
          <DatabaseValueBulkEdit
            contentId={contentId}
            value={value}
            onBulkEditChange={handleBulkEditChange}
            ref={setInitialFocusRef}
          />
        </MenuGroup>
      )}
    />
  );
};

export default BulkEditValues;
